'use client';

import { useCallback } from 'react';

import { setWearWithAiIntent } from '../functions/wearWithAiIntent';

/**
 * Routes the user into the AI Creator Studio with a pending
 * "Wear with AI" intent (product → outfit).
 *
 * Legacy parity:
 *   `setCreatorStudioProductIntent({ listingId, outfitImageUrl })`
 *     + `purchaseNavigation.navigate('_creatorStudioIframe', {})`
 *
 * Hand-off mechanism:
 *   - Persist `{ listingId, outfitImageUrl }` in sessionStorage
 *     via `setWearWithAiIntent`. The studio root reads it on mount
 *     (`consumeWearWithAiIntent`), seeds the form, and the
 *     existing `productOutfitPending` effect routes to `/scene`.
 *   - If the user has no persona yet, the studio root bounces to
 *     `/look/intro` for setup. sessionStorage outlives that whole
 *     onboarding chain — when the user lands back on the studio
 *     root post-setup, the resume effect picks up the task.
 *   - Target is the studio root (not `/look`) so the persona-vs-
 *     no-persona branching happens in ONE place (the existing
 *     redirect effect in `ai-creator-studio/page.tsx`) instead of
 *     being duplicated here.
 */
export function useNavigateToWearWithAi() {
  return useCallback(
    ({
      listingId,
      outfitImageUrl,
    }: {
      listingId: string;
      outfitImageUrl?: string;
    }) => {
      // Persist the intent in sessionStorage BEFORE navigating —
      // the studio's onboarding chain (intro → setup-outfit → ...)
      // drops query params between pushes, so we can't rely on
      // the URL alone. The outfit step reads the intent and seeds
      // the look context; intermediate pages can `peek` to know
      // "we're mid wear-with-ai task" without consuming it.
      setWearWithAiIntent({ listingId, outfitImageUrl });

      // Target is the studio root, NOT `/look` directly. The
      // root's `useEffect` chain decides what to do:
      //   - User has a persona → consume the intent, seed the
      //     form, auto-route to `/scene` with the product
      //     pre-selected as the outfit.
      //   - User has no persona → the root's existing
      //     redirect-to-onboarding effect bounces to
      //     `/look/intro`. The intent stays in sessionStorage
      //     through the entire intro → setup-outfit → ...
      //     onboarding chain; when the user lands back on
      //     the studio root (post-setup), the consume effect
      //     fires + routes to `/scene` so the task they
      //     started picks up where it left off.
      // Hard navigation via `window.location` — the Next
      // router's `push`/`replace`/`back` all interact badly
      // with the intercepted-modal `@modal/(.)products/...`
      // slot: `back` pops the modal but swallows the
      // subsequent `push`; `replace` keeps the modal layer in
      // some cases. A full-page navigation guarantees the
      // modal is torn down and the studio mounts cleanly.
      window.location.href = '/ai-creator-studio';
    },
    [],
  );
}
